<template>
    <flex-container>
        <flex-container align-items="center">
            <flex-container :size="5" :padding="Size.sm">
                <text-decorator :size="FontSize.xs" weight="medium">
                    De A.I. Medewerker kan jouw documenten gebruiken om efficiënte klantondersteuning te bieden.
                    Upload PDF-, Word- of TXT bestanden om uw bedrijf beter te begrijpen.
                </text-decorator>
            </flex-container>

            <flex-container :size="5">
                <input-group v-model="document">
                    <template #input>
                        <file-drop-zone-input
                            direct-upload
                            :direct-upload-callback="uploadDocuments"
                            :accept="acceptedFileTypes.join(',')"
                            variant="row"
                            multiple
                        >
                            <template #icon>
                                <svg-icon
                                    :icon="AddIcon"
                                    :color="'primary'"
                                    :size="IconSize.lg"
                                />
                            </template>
                            <text-decorator>Document toevoegen</text-decorator>
                        </file-drop-zone-input>
                    </template>
                </input-group>
            </flex-container>
        </flex-container>

        <horizontal-line no-margin />

        <flex-container
            justify-content="flex-end"
            align-items="center"
            :gap="null"
        >
            <flex-container size="auto" align-items="center">
                <text-decorator :size="FontSize.xs" weight="medium">
                    Documenten toedienen:
                </text-decorator>
                <flex-container size="auto">
                    <input-group
                        v-model="assistent.behavior.documentsEnabled"
                        inline
                    >
                        <template #input>
                            <switch-input />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>
            <text-decorator :size="FontSize.xs">
                {{ assistent.behavior.documentsEnabled ? 'Ingeschakeld' : 'Uitgeschakeld' }}
            </text-decorator>
        </flex-container>
        <card-normal
            v-if="assistent.behavior.documents.length > 0"
            without-padding
            :with-overflow="false"
        >
            <document-list
                :documents="assistent.behavior.documents"
                @delete:document="deleteDocument"
            />
        </card-normal>
    </flex-container>
</template>

<script setup lang="ts">
    import {
        FlexContainer,
        FontSize,
        IconSize,
        InputGroup,
        NotificationBlock, Size,
        SwitchInput,
        TextDecorator,
        useNotify,
    } from '@keyvue/one-ui';
    import {
        type Assistent,
        AssistentDocument,
        DocumentId,
    } from '@keyvue/one-api-client';
    import AddIcon from '@keyvue/one-material-icons/icons/add.svg?component';
    import { computed, type Ref, ref, watch } from 'vue';
    import WarningIcon from '@keyvue/one-material-icons/icons/warning.svg?component';
    import CheckIcon from '@keyvue/one-material-icons/icons/check.svg?component';
    import DocumentList from '@/ai/assistent/components/forms/knowledge/DocumentList.vue';

    const props = defineProps<{ modelValue: Assistent }>();
    const emit = defineEmits<{
        (e: 'update:modelValue', value: Assistent): void;
    }>();

    const assistent = ref(props.modelValue) as Ref<Assistent>;

    const document = ref(null);

    const { notify } = useNotify();

    const acceptedFileTypes = computed(() => {
        return [
            '.pdf',
            '.doc',
            '.docx',
            '.txt',
        ];
    });

    watch(props.modelValue, (value) => {
        assistent.value = value;
    });

    watch(assistent, (value: Assistent) => {
        emit('update:modelValue', value);
    }, { deep: true });

    const uploadDocuments = async (files: File[]) => {
        const validFiles = files.filter((file: File) => {
            return acceptedFileTypes.value.some((type) => file.name.endsWith(type));
        });

        validFiles.forEach((file) => {
            assistent.value.behavior.documents.push(
                AssistentDocument.fromObject({
                    id: DocumentId.generate(),
                    file,
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                }),
            );
        });

        if (validFiles.length) {
            notify({
                component: NotificationBlock,
                props: {
                    color: 'green-500',
                    icon: CheckIcon,
                    title: 'Bestanden succesvol geüpload!',
                    text: 'Sla de assistent op om de wijzigingen door te voeren.',
                },
                duration: 4000,
            });
        }

        const invalidFiles = files.length - validFiles.length;
        if (invalidFiles > 0) {
            notify({
                component: NotificationBlock,
                props: {
                    color: 'red-500',
                    icon: WarningIcon,
                    title: 'Bestanden overgeslagen',
                    text: `${invalidFiles} ongeldig(e) bestand(en) overgeslagen.`,
                },
                duration: 4000,
            });
        }
    };

    function deleteDocument(deletedDocument: AssistentDocument) {
        try {
            const updatedDocuments = assistent.value.behavior.documents.filter(
                (doc) => doc.id !== deletedDocument.id,
            );

            assistent.value.behavior = {
                ...assistent.value.behavior,
                documents: updatedDocuments,
            };
            openDeleteNotification(true, deletedDocument.fileName || '');
        } catch (error) {
            openDeleteNotification(false, deletedDocument.fileName || '');
        }
    }

    const openDeleteNotification = (success: boolean, name: string) => {
        const notificationContent = success
            ? {
                color: 'green-500',
                icon: CheckIcon,
                title: 'Gelukt!',
                text: `We hebben document ${name} verwijderd`,
            }
            : {
                color: 'red-500',
                icon: WarningIcon,
                title: `We konden document ${name} niet verwijderen!`,
                text: 'Probeer het later nog een keer.',
            };

        notify({
            component: NotificationBlock,
            props: notificationContent,
            duration: 5000,
        });
    };
</script>
